<template>
  <div class="app" :class="`current-scene-${currentScene}`">
    <div class="stage">
      <div class="stage__padding">
        <div class="stage__inner">
          <h1 class="visuallyhidden">Kids Work - Playwright</h1>
          <button
            class="scene__nav scene__nav--previous button"
            v-if="currentScene > 1"
            @click="previousScene()"
          >
            <span class="scene__nav-inner">Back</span>
          </button>
          <button
            class="scene__nav scene__nav--next button"
            @click="nextScene()"
            v-if="!beginButton"
            tabindex="2"
          >
            <span class="scene__nav-inner">{{ nextButtonText }}</span>
          </button>
          <button
            class="mute"
            :class="{
              'muted': muted
            }"
            @click="toggleMute()"
          >toggle mute</button>
          <button class="scene__begin button" @click="begin()" v-if="beginButton">Begin</button>
          <!-- Scene 1 -->
          <div
            class="scene scene-1"
            :class="{
              'active': currentScene === 1,
              'active-play': currentScene === 1 && !beginButton
            }"
          >
            <div class="scene__background"></div>
            <Manager></Manager>

            <div class="scene-1__text">
              <h2 class="visuallyhidden">Kids Work - Scene 1</h2>
              <p
                :class="{
                'fadeIn': trackTime >= 3.5
              }"
              >So you want to be a playwright.</p>
              <p
                :class="{
                'fadeIn': trackTime >= 6
              }"
              >Your first rehearsal begins in 20 minutes.</p>
              <p
                :class="{
                'fadeIn': trackTime >= 8.5
              }"
              >Don't forget your script.</p>
            </div>
          </div>

          <!-- Scene 2 -->
          <div
            class="scene scene-2"
            :class="{
              'active': currentScene === 2
            }"
          >
            <div class="scene__background"></div>
            <div class="scene__stage-manager-arm"></div>
            <div class="scene__stage-manager"></div>
            <div class="scene__wrigley"></div>
            <div class="scene__wrigley-tail"></div>
            <div class="scene-2__text">
              <p
                :class="{
                'fadeIn': trackTime >= 0.5
              }"
              >Oh No! Wrigley has chewed your script.</p>
              <p
                :class="{
                'fadeIn': trackTime >= 2.5
              }"
              >Use your mouse to pick up the pages.</p>
            </div>
            <button
              class="scene__nav scene__nav--pages button"
              :class="{
                'activate': trackTime >= 3.5
              }"
              v-if="currentScene === 2"
              @click="nextScene()"
            >
              <span class="scene__nav-inner">Pick up the Pages</span>
            </button>
          </div>

          <!-- Scene 3 -->
          <div
            class="scene scene-3"
            :class="{
              'active': currentScene === 3
            }"
          >
            <div class="scene__background"></div>
            <div class="scene-3__text">
              <p>Whew! You saved your script from Wrigley. Touch the pages with your mouse to read the definitions for each page. After you learn the parts of the script, it's time to rehearse!</p>
            </div>
            <div class="scene-3__scripts">
              <!-- PAGE 1 -->
              <a href="#" class="script-page title-page" @mouseover="toggleAudioByName('swoosh')">
                <p class="uppercase">Cinderella and the Magic Crystal</p>
                <p class="normal">by Joe Writer</p>
              </a>
              <div class="definition title-page">
                <h4>Title Page</h4>
                <p>The title gives the audience an idea of what the play is about. The playwright's name is also on this page.</p>
              </div>
              <!-- PAGE 2 -->
              <a href="#" class="script-page characters" @mouseover="toggleAudioByName('swoosh')">
                <p class="upercase">CAST OF CHARACTERS</p>
                <p class="normal">
                  Cinderella
                  <br />Torchar
                </p>
              </a>
              <div class="definition characters">
                <h4>Cast of Characters</h4>
                <p>
                  The list of all of the
                  <strong>characters</strong> or
                  <strong>roles</strong> in the play. Descriptions of each character may also be included.
                </p>
              </div>
              <!-- PAGE 3 -->
              <a href="#" class="script-page background" @mouseover="toggleAudioByName('swoosh')">
                <p class="upercase">BACKGROUND</p>
                <p class="normal">Settings</p>
              </a>
              <div class="definition background">
                <h4>Background</h4>
                <p>
                  A brief overview of the play that may include information about what has already taken place. The
                  <strong>setting</strong> explains when and where your story happens.
                </p>
              </div>
              <!-- PAGE 4 -->
              <a href="#" class="script-page acts" @mouseover="toggleAudioByName('swoosh')">
                <p>ACT I, Scene One</p>
              </a>
              <div class="definition acts">
                <h4>Acts</h4>
                <p>
                  A grouping of
                  <strong>scenes</strong> performed in sequence. A play can have one or more acts. They are numbered in Roman numerals:I, II, III.
                </p>
              </div>
              <!-- PAGE 5 -->
              <a href="#" class="script-page scenes" @mouseover="toggleAudioByName('swoosh')">
                <p>ACT II, Scene One</p>
              </a>
              <div class="definition scenes">
                <h4>Scenes</h4>
                <p>
                  A small group of
                  <strong>lines</strong> that show action in the story. A scene usually changes when there is a new situation in the play.
                </p>
              </div>
              <!-- PAGE 6 -->
              <a href="#" class="script-page lines" @mouseover="toggleAudioByName('swoosh')">
                <p>ACT III, Scene One</p>
              </a>
              <div class="definition lines">
                <h4>Lines</h4>
                <p>
                  The lines in the script are
                  <strong>dialogue</strong> and
                  <strong>stage directions</strong>. Dialogue is the term for the words that the characters speak. Stage directions are written to show the movement or emotion of the character.
                </p>
              </div>
            </div>
            <div class="scene-3__definition-instructions">
              <h4>Parts of a Script</h4>
              <p>
                Touch the pages with your mouse to read the definitions for each page. After you learn the parts of the script, press the
                <strong>Rehearse</strong> button.
              </p>
              <p class="clue">
                A
                <strong>rehearsal</strong> is when the actors practice their lines.
              </p>
            </div>

            <button
              class="scene__nav scene__nav--reherese button"
              v-if="currentScene === 3"
              @click="nextScene()"
            >
              <span class="scene__nav-inner">Rehearse</span>
            </button>
          </div>

          <!-- Scene 4 -->
          <div
            class="scene scene-4"
            :class="{
              'active': currentScene === 4
            }"
          >
            <div class="scene-4__text">
              <p>Rehearsal is about to begin. Take a closer look by clicking the title page.</p>
            </div>

            <button
              class="scene__nav scene-4__title-page"
              v-if="currentScene === 4"
              @click="nextScene()"
            >
              <p class="title">Cinderella and the Magic Crystal</p>
              <p class="author">by Joe Writer</p>
            </button>
          </div>

          <!-- Scene 5 -->
          <div
            class="scene scene-5"
            :class="{
              'active': currentScene === 5
            }"
          >
            <div class="scene-5__text">
              <p>The background page will help the actors understand the scene. Listen and read along.</p>
            </div>

            <div class="scene-5__image">
              <p>
                <strong>CAST OF CHARACTERS</strong>
              </p>
              <ul>
                <li>Cinderella</li>
                <li>Torchar</li>
              </ul>
            </div>

            <div class="scene-5__content">
              <p>
                <strong>BACKGROUND:</strong> Cinderella married the Prince and lived happily ever after. That's only in the fairy tale. In this story, Cinderella thought long and hard about becoming a dutiful Princess and said goodbye to the handsome, charming Prince. Those glass slippers gave her blisters anyway.
              </p>
              <p>Cinderella wanted to study Geology so she traveled the world far and wide, climbing mountains and exploring caves. She hoped to find a magic crystal that would bring good health to the sick children in her village. On this day, Cinderella finds more than rocks in a cave. She meets a grouchy, fire-breathing dragon named Torchar, who just happened to be on vacation from terrorizing foreign lands.</p>
            </div>

            <div
              class="button--wrapper"
              :class="{
                'fadeIn': trackTime >= 5
            }"
            >
              <button
                @click="toggleAudioByName('listen1')"
                class="scene__nav scene__nav--listen button"
              >{{ buttonLabel }}</button>

              <button
                class="scene__nav scene__nav--next-b button"
                v-if="currentScene === 5"
                @click="nextScene()"
              >Next</button>
            </div>
          </div>

          <div
            class="scene scene-6"
            :class="{
              'active': currentScene === 6
            }"
          >
            <div class="scene-6__text">
              <p>Listen and read along as the actors rehearse Scene One.</p>
            </div>

            <div
              class="button--wrapper"
              :class="{
                'fadeIn': trackTime >= 4
            }"
            >
              <button
                @click="toggleAudioByName('listen2')"
                class="scene__nav scene__nav--listen button"
              >{{ buttonLabel }}</button>

              <button
                class="scene__nav scene__nav--next-b button"
                v-if="currentScene === 6"
                @click="nextScene()"
              >Next</button>
            </div>

            <div class="panel">
              <div class="panel-left">
                <h4>ACT I, Scene One</h4>
                <p>
                  <em>Setting: A dark cave. Cinderella enters upstage left with a brightly lit lantern. Torchar is not seen until the end of Scene One.</em>
                </p>
                <p class="torchar">
                  <strong>TORCHAR:</strong>
                  <span>
                    <em>(Loudly)</em> Who is in my cave!
                  </span>
                </p>
                <p class="cinderella">
                  <strong>CINDERELLA:</strong>
                  <span>
                    <em>(A little nervously)</em> I'm Cinderella. Who are you, may I ask?
                  </span>
                </p>
                <p class="torchar">
                  <strong>TORCHAR:</strong>
                  <span>
                    <em>(Angrily)</em> What are you doing here?
                  </span>
                </p>
                <p class="cinderella">
                  <strong>CINDERELLA:</strong>
                  <span>
                    <em>(Walks to center stage)</em> I'm a Geologist and I study rocks.
                  </span>
                </p>
                <p class="torchar">
                  <strong>TORCHAR:</strong>
                  <span>
                    <em>(Demanding)</em> Leave my cave at once!
                  </span>
                </p>
              </div>
              <div class="panel-right">
                <p class="cinderella">
                  <strong>CINDERELLA:</strong>
                  <span>
                    <em>(Annoyed at the dragon's rudeness)</em> Well, excuse me, but you are very rude. (Pause) Who are you?
                  </span>
                </p>
                <p class="torchar">
                  <strong>TORCHAR:</strong>
                  <span>
                    <em>(Very angry)</em> I am Torchar the Dragon!
                    <em>(Roars)</em>
                  </span>
                </p>
                <p>The dragon blows fire and misses Cinderella. The fire melts a cave rock into a glowing crystal.</p>
                <p class="torchar">
                  <strong>TORCHAR:</strong>
                  <span>
                    <em>(Surprised)</em> Huh?
                  </span>
                </p>
                <p class="cinderella">
                  <strong>CINDERELLA:</strong>
                  <span>
                    <em>(Gasps)</em> Look! (Puts hands on the rock) You found a magic crystal!
                  </span>
                </p>
              </div>
            </div>
          </div>
          <!-- Scene 7-->
          <div
            class="scene scene-7"
            :class="{
              'active': currentScene === 7
            }"
          >
            <div
              class="scene-7__text"
              :class="{
                'transition': trackTime >= 3.5
              }"
            >
              <p>Listen and read along as the actors rehearse Scene One.</p>

              <button class="scene__nav scene__nav--listen button">Listen</button>
              <button class="scene__nav scene__nav--next-b button">Next</button>
            </div>

            <div
              class="scene-7__content"
              :class="{
                'transition': trackTime >= 3.5
              }"
            >
              <div class="panel">
                <div class="panel-left">
                  <h4>ACT I, Scene One</h4>
                  <p>
                    <em>Setting: A dark cave. Cinderella enters upstage left with a brightly lit lantern. Torchar is not seen until the end of Scene One.</em>
                  </p>
                  <p class="torchar">
                    <strong>TORCHAR:</strong>
                    <span>
                      <em>(Loudly)</em> Who is in my cave!
                    </span>
                  </p>
                  <p class="cinderella">
                    <strong>CINDERELLA:</strong>
                    <span>
                      <em>(A little nervously)</em> I'm Cinderella. Who are you, may I ask?
                    </span>
                  </p>
                  <p class="torchar">
                    <strong>TORCHAR:</strong>
                    <span>
                      <em>(Angrily)</em> What are you doing here?
                    </span>
                  </p>
                  <p class="cinderella">
                    <strong>CINDERELLA:</strong>
                    <span>
                      <em>(Walks to center stage)</em> I'm a Geologist and I study rocks.
                    </span>
                  </p>
                  <p class="torchar">
                    <strong>TORCHAR:</strong>
                    <span>
                      <em>(Demanding)</em> Leave my cave at once!
                    </span>
                  </p>
                </div>
                <div class="panel-right">
                  <p class="cinderella">
                    <strong>CINDERELLA:</strong>
                    <span>
                      <em>(Annoyed at the dragon's rudeness)</em> Well, excuse me, but you are very rude. (Pause) Who are you?
                    </span>
                  </p>
                  <p class="torchar">
                    <strong>TORCHAR:</strong>
                    <span>
                      <em>(Very angry)</em> I am Torchar the Dragon!
                      <em>(Roars)</em>
                    </span>
                  </p>
                  <p>The dragon blows fire and misses Cinderella. The fire melts a cave rock into a glowing crystal.</p>
                </div>
              </div>
              <div class="scene-7__bite"></div>

              <div
                class="scene-7__wrigley-ate"
                :class="{
                'activate': trackTime >= 3.5
              }"
              >
                <p class="oh-no">Oh no! Wrigley ate the ending of Scene One.</p>

                <Wrigley :class="{
                'animate': trackTime >= 3.5
              }"></Wrigley>
              </div>

              <div
                class="scene-7__wrigley-rewrite"
                :class="{
                  'activate': trackTime >= 7.5
                }"
              >
                <p
                  class="dont-worry"
                >Don't worry! Rewriting is a common practice for playwrights. Pick up the pencil to write a different ending.</p>

                <button
                  class="pencil-button"
                  :class="{
                  'activate': trackTime >= 12.5
                  }"
                  v-if="currentScene === 7"
                  @click="nextScene()"
                >
                  <span class="scene__nav-inner">Pencil and Paper</span>
                </button>
              </div>
            </div>
          </div>
          <!-- Scene 8 -->
          <div
            class="scene scene-8"
            :class="{
              'active': currentScene === 8
            }"
          >
            <div class="scene-8__text">
              <p>Keep in mind that dialogue is short and to the point. After you type the new lines, enter them into your script.</p>
            </div>

            <div class="panel">
              <div class="panel-left">
                <h4>ACT I, Scene One</h4>
                <p>
                  <em>Setting: A dark cave. Cinderella enters upstage left with a brightly lit lantern. Torchar is not seen until the end of Scene One.</em>
                </p>
                <p class="torchar">
                  <strong>TORCHAR:</strong>
                  <span>
                    <em>(Loudly)</em> Who is in my cave!
                  </span>
                </p>
                <p class="cinderella">
                  <strong>CINDERELLA:</strong>
                  <span>
                    <em>(A little nervously)</em> I'm Cinderella. Who are you, may I ask?
                  </span>
                </p>
                <p class="torchar">
                  <strong>TORCHAR:</strong>
                  <span>
                    <em>(Angrily)</em> What are you doing here?
                  </span>
                </p>
                <p class="cinderella">
                  <strong>CINDERELLA:</strong>
                  <span>
                    <em>(Walks to center stage)</em> I'm a Geologist and I study rocks.
                  </span>
                </p>
                <p class="torchar">
                  <strong>TORCHAR:</strong>
                  <span>
                    <em>(Demanding)</em> Leave my cave at once!
                  </span>
                </p>
              </div>
              <div class="panel-right">
                <p class="cinderella">
                  <strong>CINDERELLA:</strong>
                  <span>
                    <em>(Annoyed at the dragon's rudeness)</em> Well, excuse me, but you are very rude.
                    <em>(Pause)</em> Who are you?
                  </span>
                </p>
                <p class="torchar">
                  <strong>TORCHAR:</strong>
                  <span>
                    <em>(Very angry)</em> I am Torchar the Dragon!
                    <em>(Roars)</em>
                  </span>
                </p>
                <p>The dragon blows fire and misses Cinderella. The fire melts a cave rock into a glowing crystal.</p>

                <form @submit.prevent="onSubmit">
                  <label for="dialogueA" class="torchar">Torchar</label>
                  <textarea
                    id="dialogueA"
                    ref="dialoguefieldA"
                    type="text"
                    name="torchar-message"
                    maxlength="95"
                  ></textarea>
                  <label for="dialogueB" class="cinderella">Cinderella</label>
                  <textarea
                    id="dialogueB"
                    ref="dialoguefieldB"
                    type="text"
                    name="cinderella-message"
                    maxlength="95"
                  ></textarea>
                  <input type="submit" value="Enter" class="scene__nav enter button" />
                </form>
              </div>
            </div>
          </div>
          <!-- Scene 9 -->
          <div
            class="scene scene-9"
            :class="{
              'active': currentScene === 9
            }"
          >
            <div class="scene-9__text">
              <p>Rehearse the ending to Scene One. When you are done, end the activity to return to the Job Play area.</p>
            </div>

            <div class="panel">
              <div class="panel-left">
                <h4>ACT I, Scene One</h4>
                <p>
                  <em>Setting: A dark cave. Cinderella enters upstage left with a brightly lit lantern. Torchar is not seen until the end of Scene One.</em>
                </p>
                <p class="torchar">
                  <strong>TORCHAR:</strong>
                  <span>(Loudly) Who is in my cave!</span>
                </p>
                <p class="cinderella">
                  <strong>CINDERELLA:</strong>
                  <span>(A little nervously) I'm Cinderella. Who are you, may I ask?</span>
                </p>
                <p class="torchar">
                  <strong>TORCHAR:</strong>
                  <span>(Angrily) What are you doing here?</span>
                </p>
                <p class="cinderella">
                  <strong>CINDERELLA:</strong>
                  <span>(Walks to center stage) I'm a Geologist and I study rocks.</span>
                </p>
                <p class="torchar">
                  <strong>TORCHAR:</strong>
                  <span>(Demanding) Leave my cave at once!</span>
                </p>
              </div>
              <div class="panel-right">
                <p class="cinderella">
                  <strong>CINDERELLA:</strong>
                  <span>(Annoyed at the dragon's rudeness) Well, excuse me, but you are very rude. (Pause) Who are you?</span>
                </p>
                <p class="torchar">
                  <strong>TORCHAR:</strong>
                  <span>(Very angry) I am Torchar the Dragon! (Roars)</span>
                </p>
                <p>The dragon blows fire and misses Cinderella. The fire melts a cave rock into a glowing crystal.</p>

                <form>
                  <label for="messageA" class="torchar">Torchar</label>
                  <textarea
                    id="messageA"
                    name="torchar-message"
                    maxlength="95"
                    disabled
                    v-model="dialogueA"
                  ></textarea>
                  <label for="messageB" class="cinderella">Cinderella</label>
                  <textarea
                    id="messageB"
                    name="cinderella-message"
                    maxlength="95"
                    disabled
                    v-model="dialogueB"
                  ></textarea>
                  <input type="submit" value="END" class="visuallyhidden" />
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Manager from "@/components/Manager";
import Wrigley from "@/components/Wrigley";

import { mapState, mapMutations, mapActions } from "vuex";
export default {
  name: "app",
  components: {
    Manager,
    Wrigley
  },
  data() {
    return {
      dialogueA: "",
      dialogueB: "",
      isActiveBackground: false,
      isActiveScene: false
    };
  },
  computed: {
    ...mapState([
      "numScenes",
      "currentScene",
      "beginButton",
      "muted",
      "trackTime",
      "buttonLabel"
    ]),
    nextButtonText() {
      if (this.currentScene < this.numScenes) {
        return "Next";
      } else {
        return "End";
      }
    }
  },

  methods: {
    ...mapMutations(["HIDE_BEGIN_BUTTON", "RESET_TRACK_INTERVAL"]),
    ...mapActions([
      "begin",
      "previousScene",
      "nextScene",
      "toggleAudioByName",
      "playAudioByName",
      "toggleMute"
    ]),

    toggleBackground: function() {
      this.isActiveBackground = !this.isActiveBackground;
    },
    toggleScene: function() {
      this.isActiveScene = !this.isActiveScene;
    },
    onSubmit() {
      this.dialogueA = this.$refs.dialoguefieldA.value;
      this.dialogueB = this.$refs.dialoguefieldB.value;
      this.nextScene();
    }
  }
};
</script>

<style lang="scss">
body {
  background-color: #fff;
}
</style>
